import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  Injector,
} from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { AppUserService } from 'src/app/shared/services/app-user.service';
import { AppState, AppUserState } from 'src/app/shared/store/states/app.state';
import { select, Store } from '@ngrx/store';
import { SubscriptionHelper } from 'src/app/shared/helpers/subscription.helper';
import { selectCurrentUser } from 'src/app/shared/store/selectors/user.selector';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { UAMAuthService } from 'src/app/shared/services/uam-auth.service';
import { ProjectsService } from 'src/app/shared/services/projects.service';
import { ThemeService } from '../../shared/services/theme.service';
import { isPublicRoute } from 'src/app/shared/helpers/public-route.helper';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() title1 = environment.application.admin.title1;
  @Input() title2 = environment.application.admin.title2;
  isDevEnvironment = false;
  unreadNotifications = 6;
  notificationIcon = 'notifications_none';
  isAdminRoute = false;
  isAuthRoute = false;
  isRegistrationRoute = false;
  currentUser!: AppUserState;
  organization = '';
  projects: any[] = [];
  selectedProject: any = null;
  userMenuOpen = false;
  dropdownWidth = 280;
  isPreviewRoute = false;
  isPublicRoute = false;
  isAdmin = false;
  version = 'v3.0.0';
  currentTheme: string = '';
  environment = environment;
  private projectsService: ProjectsService | null = null;
  private adminSetupDone = false;

  @ViewChild('userMenuTrigger') userMenuTrigger!: ElementRef<HTMLButtonElement>;

  constructor(
    private store: Store<AppState>,
    private platform: Platform,
    public dialog: MatDialog,
    private appUser: AppUserService,
    private subMgr: SubscriptionHelper,
    private ngxCookieService: CookieService,
    private uamAuthService: UAMAuthService,
    private router: Router,
    private injector: Injector,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects;
        this.updateRouteFlags(url);
        if (this.isAdminRoute && navigator.onLine) {
          this.loadMaterialIconsStylesheet();
        }
        this.setupAdminProjectsIfNeeded();
      });

    this.currentTheme = this.themeService.getCurrentThemeName();

    this.subMgr.add(
      this.themeService.themeChanged$.subscribe(() => {
        this.currentTheme = this.themeService.getCurrentThemeName();
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy() {
    this.subMgr.destroy();
  }

  ngOnInit(): void {
    this.version = `v${environment.version}`;
    this.updateRouteFlags(this.router.url);

    if (this.isAdminRoute && navigator.onLine) {
      this.loadMaterialIconsStylesheet();
    }

    this.setupAdminProjectsIfNeeded();
  }

  onProjectChanged(project: any) {
    if (project && project !== this.selectedProject) {
      this.projectsService?.setSelectedProject(project);
    }
  }

  goHome() {
    if (this.isAdminRoute) {
      this.router.navigateByUrl('/admin');
    } else {
      this.router.navigateByUrl('/public');
    }
  }
  get safeAreaHeight() {
    if (!this.platform.isBrowser) {
      return 0;
    }
    alert('is not browser');
    const height = window.innerHeight * window.devicePixelRatio;
    const safeAreaHeight = height - 44;
    return safeAreaHeight;
  }

  logout() {
    localStorage.clear();
    sessionStorage.clear();
    this.ngxCookieService.delete(
      'uam-id-token',
      '/',
      environment.cookieInfo.domain
    );
    this.ngxCookieService.delete(
      'uam-refresh-token',
      '/',
      environment.cookieInfo.domain
    );
    this.ngxCookieService.delete(
      'uam-device-key',
      '/',
      environment.cookieInfo.domain
    );
    document.location.href = environment.uamUrl + 'auth/logout';
  }

  selectProject(projectName: string) {
    this.selectedProject = projectName;
  }
  applyProjectSelection() {
    this.appUser.setSelectedProject(this.selectedProject);
  }

  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
    if (this.userMenuOpen && this.userMenuTrigger?.nativeElement) {
      this.dropdownWidth = this.userMenuTrigger.nativeElement.offsetWidth;
      this.cdr.detectChanges();
    }
  }

  signOut(): void {
    this.logout();
  }

  get userInitials(): string {
    return this.getUserInitials();
  }

  get userFullName(): string {
    if (!this.currentUser) return '';
    const first = (
      this.currentUser.name ||
      this.currentUser['given_name'] ||
      ''
    ).trim();
    const last = (
      this.currentUser.family_name ||
      this.currentUser.lastName ||
      ''
    ).trim();
    return [first, last].filter(Boolean).join(' ');
  }

  get userEmail(): string {
    if (!this.currentUser) return '';
    return this.currentUser.organization || this.currentUser.email || '';
  }

  getUserInitials(): string {
    if (!this.currentUser) return '';
    const first = (
      this.currentUser.name ||
      this.currentUser['given_name'] ||
      ''
    ).trim();
    const last = (
      this.currentUser.family_name ||
      this.currentUser.lastName ||
      ''
    ).trim();
    const a = first ? first.charAt(0) : '';
    const b = last ? last.charAt(0) : '';
    const initials = (a + b).toUpperCase();
    return initials || '';
  }

  goToApplicationsLauncher() {
    window.location.href = environment.uamUrl;
  }

  private updateRouteFlags(url: string): void {
    this.isPublicRoute = isPublicRoute(url);
    this.isAdminRoute = !this.isPublicRoute && url.startsWith('/admin');
    this.isAuthRoute = url.startsWith('/auth');
    this.isPreviewRoute = url.includes('previewId=');
    this.isRegistrationRoute = url.startsWith('/registration');
  }

  /** Wire projects/user subscriptions once when entering an admin route. */
  private setupAdminProjectsIfNeeded(): void {
    if (!this.isAdminRoute || this.adminSetupDone) {
      return;
    }
    this.adminSetupDone = true;

    this.projectsService = this.injector.get(ProjectsService);
    this.uamAuthService.checkAuthentication().subscribe();
    void this.projectsService.ensureLoaded();

    this.subMgr.add(
      this.projectsService.projects$
        .pipe(filter((projects) => projects.length > 0))
        .subscribe((projects) => {
          this.projects = projects;
          if (!this.projectsService?.selectedProject) {
            this.projectsService?.initializeSelectedProject();
          }
        })
    );

    this.subMgr.add(
      this.projectsService.selectedProject$
        .pipe(filter((project) => !!project))
        .subscribe((selectedProject) => {
          const projectWrapper = this.projects.find(
            (p) => p.project === selectedProject
          );
          if (projectWrapper) {
            this.selectedProject = projectWrapper;
          }
        })
    );

    this.subMgr.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          if (this.projectsService?.selectedProject) {
            this.selectedProject = this.projectsService.selectedProject;
          }
        })
    );

    this.subMgr.add(
      this.store
        .pipe(
          select(selectCurrentUser),
          filter((currentUser) => !!currentUser && currentUser.sub !== '')
        )
        .subscribe((currentUser: AppUserState) => {
          this.currentUser = currentUser;
          if (
            this.currentUser.role === 'pavcon' ||
            this.currentUser.role === 'organization-admin' ||
            this.currentUser.role === 'organization-base' ||
            this.currentUser.role === 'organization-user'
          ) {
            this.isAdmin = true;
          }
        })
    );

  }

  private loadMaterialIconsStylesheet(): void {
    if (document.getElementById('material-icons-stylesheet')) {
      return;
    }

    const link = document.createElement('link');
    link.id = 'material-icons-stylesheet';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
    document.head.appendChild(link);
  }
}
